/**
 * Detect SPA / API deploys so we can prompt a soft reload.
 * Frontend: /version.json (written at Vite build).
 * API: GET /health/live gitCommit (Railway fingerprint).
 */

import { API_BASE_URL } from '@/lib/api'

export type AppVersionSnapshot = {
  frontendBuildId: string | null
  apiGitCommit: string | null
}

export type AppUpdateChange = {
  frontendChanged: boolean
  apiChanged: boolean
}

const VERSION_JSON_PATH = '/version.json'

/** Normalize short SHAs for stable compare. */
export function normalizeBuildId(raw: unknown): string | null {
  if (typeof raw !== 'string') return null
  const trimmed = raw.trim()
  if (!trimmed || trimmed === 'dev' || trimmed === 'unknown') return null
  return trimmed.length > 12 ? trimmed.slice(0, 12) : trimmed
}

export function detectAppUpdateChange(
  baseline: AppVersionSnapshot,
  next: AppVersionSnapshot,
): AppUpdateChange {
  const frontendChanged = Boolean(
    baseline.frontendBuildId &&
      next.frontendBuildId &&
      baseline.frontendBuildId !== next.frontendBuildId,
  )
  const apiChanged = Boolean(
    baseline.apiGitCommit &&
      next.apiGitCommit &&
      baseline.apiGitCommit !== next.apiGitCommit,
  )
  return { frontendChanged, apiChanged }
}

export async function fetchFrontendBuildId(
  fetcher: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetcher(`${VERSION_JSON_PATH}?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
    })
    if (!res.ok) return null
    const data = (await res.json()) as { buildId?: string }
    return normalizeBuildId(data.buildId)
  } catch {
    return null
  }
}

export async function fetchApiGitCommit(
  fetcher: typeof fetch = fetch,
): Promise<string | null> {
  try {
    const res = await fetcher(`${API_BASE_URL}/health/live`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    if (!res.ok) return null
    const data = (await res.json()) as { gitCommit?: string | null }
    return normalizeBuildId(data.gitCommit)
  } catch {
    return null
  }
}

export async function fetchAppVersionSnapshot(
  fetcher: typeof fetch = fetch,
): Promise<AppVersionSnapshot> {
  const [frontendBuildId, apiGitCommit] = await Promise.all([
    fetchFrontendBuildId(fetcher),
    fetchApiGitCommit(fetcher),
  ])
  return { frontendBuildId, apiGitCommit }
}
